import { en, type MessageKey } from './en';
import { de } from './de';

export type { MessageKey };
export { en, de };

export type Locale = 'en' | 'de';
export const LOCALES: readonly Locale[] = ['en', 'de'] as const;

/** What the user picked in Settings. `system` follows the browser. */
export type LocalePreference = Locale | 'system';

export const CATALOGUES: Record<Locale, Record<MessageKey, string>> = { en, de };

export type TranslateParams = Record<string, string | number>;

const PLACEHOLDER = /\{(\w+)\}/g;

function isLocale(value: string): value is Locale {
  return (LOCALES as readonly string[]).includes(value);
}

/**
 * English is the default. German wins only when the browser's *primary*
 * language is German — having German somewhere further down the list means
 * the user prefers something else first.
 */
export function detectLocale(languages?: readonly string[] | string): Locale {
  const list =
    typeof languages === 'string'
      ? [languages]
      : (languages ?? (typeof navigator === 'undefined' ? [] : [navigator.language]));
  const primary = list[0];
  if (primary && primary.toLowerCase().startsWith('de')) return 'de';
  return 'en';
}

export function resolveLocale(
  preference: LocalePreference,
  languages?: readonly string[] | string,
): Locale {
  if (preference !== 'system' && isLocale(preference)) return preference;
  return detectLocale(languages);
}

/**
 * Look up a message and fill in `{placeholders}`.
 *
 * When `params.count` is 1 and a `<key>_one` variant exists, the singular is
 * used. Both supported languages need nothing more than that.
 */
export function translate(locale: Locale, key: MessageKey, params?: TranslateParams): string {
  const catalogue = CATALOGUES[locale];
  let template: string | undefined;

  if (params && Number(params.count) === 1) {
    template = catalogue[`${key}_one` as MessageKey];
  }
  template ??= catalogue[key] ?? CATALOGUES.en[key];

  // A missing key is a bug, not a user-facing state — surface it loudly
  // instead of rendering an empty string.
  if (template === undefined) return key;
  if (!params) return template;

  return template.replace(PLACEHOLDER, (match, name: string) => {
    const value = params[name];
    return value === undefined ? match : String(value);
  });
}

export interface Translator {
  (key: MessageKey, params?: TranslateParams): string;
  readonly locale: Locale;
}

export function createTranslator(locale: Locale): Translator {
  const t = ((key: MessageKey, params?: TranslateParams) => translate(locale, key, params)) as {
    (key: MessageKey, params?: TranslateParams): string;
  } & {
    locale: Locale;
  };
  t.locale = locale;
  return t;
}

/** Every key that exists in English but is missing or empty in German. */
export function missingTranslations(locale: Locale): MessageKey[] {
  const catalogue = CATALOGUES[locale];
  return (Object.keys(en) as MessageKey[]).filter((key) => !catalogue[key]);
}
